import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Eye, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '../../components/Button';
import { DataTable } from '../../components/data-table/DataTable';
import { TableToolbar } from '../../components/data-table/TableToolbar';
import type { Column, SortSpec } from '../../components/data-table/types';
import { MenuGroup, MenuItem } from '../../components/Popover';
import { EmptyState, FilteredEmptyState } from '../../components/system-states/SystemState';
import { STAGE_TONES } from '../../components/status-tones';
import { ToneChip, type Tone } from '../../components/Tone';
import { EMPTY, formatDate, formatNumber } from '../../lib/format';
import { EMPTY_SELECTION, resolveSelection, type SelectionState } from '../../lib/selection';
import { useToasts } from '../../stores/toasts';
import { STAGE_LABELS, makeDemoRows, type DemoRow, type DemoStage } from './mock-rows';

// The demo table. 5,000 rows, so virtualization is visibly doing work and any
// scroll jank shows up in QA rather than in S15 with real candidates.
//
// Filter and sort state lives in the URL through TanStack Router's typed search
// params (06-qol §3 and §6) — which is how "copy a URL with filters applied, open
// it in a new tab, the filters are preserved" works without a bespoke serialiser.

const ROW_COUNT = 5_000;

// The demo route now hangs off the pathless app layout, so its ID carries that
// prefix even though the URL is still `/demo`.
const routeApi = getRouteApi('/app-layout/demo');

/** Ordered explicitly rather than via Object.keys, which is untyped. */
const STAGE_KEYS: readonly DemoStage[] = [
  'screening',
  'assessment',
  'interview',
  'offer',
  'rejected',
];

const STAGE_TONE: Readonly<Record<DemoStage, Tone>> = {
  screening: STAGE_TONES.screening,
  assessment: STAGE_TONES.assessment,
  interview: STAGE_TONES.interview_1,
  offer: STAGE_TONES.offer,
  rejected: STAGE_TONES.rejected,
};

export function DemoTable(): ReactNode {
  const search = routeApi.useSearch();
  const navigate = useNavigate({ from: '/demo' });
  const showToast = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);

  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const allRows = useMemo(() => makeDemoRows(ROW_COUNT), []);

  const sort: readonly SortSpec[] = useMemo(() => parseSort(search.sort), [search.sort]);

  const rows = useMemo(() => {
    const query = search.q?.trim().toLowerCase() ?? '';
    const stage = search.stage;

    const filtered = allRows.filter((row) => {
      if (stage !== undefined && row.stage !== stage) return false;
      if (query === '') return true;
      return (
        row.name.toLowerCase().includes(query) ||
        row.role.toLowerCase().includes(query) ||
        row.venture.toLowerCase().includes(query)
      );
    });

    if (sort.length === 0) return filtered;

    // Multi-column: compare in priority order, falling through on ties.
    return [...filtered].sort((a, b) => {
      for (const spec of sort) {
        const compared = compareRows(a, b, spec.columnId);
        if (compared !== 0) return spec.direction === 'asc' ? compared : -compared;
      }
      return 0;
    });
  }, [allRows, search.q, search.stage, sort]);

  const activeFilterCount = (search.q === undefined || search.q === '' ? 0 : 1) + (search.stage ? 1 : 0);

  const columns: readonly Column<DemoRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Candidate',
        isIdentity: true,
        isSortable: true,
        cell: (row) => row.name,
      },
      { id: 'role', header: 'Applied for', isSortable: true, cell: (row) => row.role },
      { id: 'venture', header: 'Venture', isSortable: true, cell: (row) => row.venture },
      {
        id: 'stage',
        header: 'Stage',
        isSortable: true,
        cell: (row) => (
          <ToneChip tone={STAGE_TONE[row.stage]}>{STAGE_LABELS[row.stage]}</ToneChip>
        ),
      },
      {
        id: 'score',
        header: 'Match score',
        isSortable: true,
        isNumeric: true,
        // Null renders as an em dash automatically — never blank, never "N/A".
        cell: (row) => (row.score === null ? null : formatNumber(row.score)),
      },
      {
        id: 'appliedAt',
        header: 'Applied',
        isSortable: true,
        cell: (row) => formatDate(row.appliedAt),
      },
      { id: 'owner', header: 'Recruiter', isSortable: true, cell: (row) => row.owner },
      { id: 'source', header: 'Source', isSortable: true, cell: (row) => row.source },
    ],
    [],
  );

  const setSearch = (next: Partial<DemoSearch>): void => {
    void navigate({ search: (current) => ({ ...current, ...next }) });
  };

  return (
    <>
      <TableToolbar
        title="Candidates"
        total={rows.length}
        totalLabel={rows.length === allRows.length ? undefined : `of ${formatNumber(allRows.length)}`}
        query={search.q ?? ''}
        onQueryChange={(q) => setSearch({ q: q === '' ? undefined : q })}
        filters={
          search.stage === undefined
            ? []
            : [
                {
                  id: 'stage',
                  label: `Stage: ${STAGE_LABELS[search.stage]}`,
                  onRemove: () => setSearch({ stage: undefined }),
                },
              ]
        }
        onClearFilters={() => setSearch({ q: undefined, stage: undefined })}
        filterMenu={
          <MenuGroup label="Stage">
            <MenuItem
              isSelected={search.stage === undefined}
              onClick={() => setSearch({ stage: undefined })}
            >
              All stages
            </MenuItem>
            {STAGE_KEYS.map((stage) => (
              <MenuItem
                key={stage}
                isSelected={search.stage === stage}
                onClick={() => setSearch({ stage })}
              >
                {STAGE_LABELS[stage]}
              </MenuItem>
            ))}
          </MenuGroup>
        }
      />

      <DataTable
        screenId="demo-candidates"
        label="Candidates"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        total={rows.length}
        sort={sort}
        onSortChange={(next) => setSearch({ sort: serialiseSort(next) })}
        selection={selection}
        onSelectionChange={setSelection}
        onOpenRow={(row) =>
          showToast({
            tone: 'info',
            message: `Would open ${row.name}`,
            detail: 'Candidate screens arrive in S14.',
          })
        }
        rowActions={(row) => (
          <>
            <Button
              variant="ghost"
              size="sm"
              icon={Eye}
              aria-label={`Preview ${row.name}`}
              onClick={() =>
                showToast({ tone: 'info', message: `Would preview ${row.name}` })
              }
            />
            <Button
              variant="ghost"
              size="sm"
              icon={Trash2}
              aria-label={`Reject ${row.name}`}
              onClick={() => showUndo(`${row.name} rejected`, () => undefined)}
            />
          </>
        )}
        bulkActions={
          <>
            <Button
              variant="quiet"
              size="sm"
              onClick={() => {
                const resolved = resolveSelection(selection);
                showToast({
                  tone: 'info',
                  message: 'Would advance the selection',
                  detail:
                    resolved.kind === 'ids'
                      ? `${resolved.ids.length} rows by id`
                      : `Everything matching the filter, minus ${resolved.excluded.length} excluded`,
                });
              }}
            >
              Advance
            </Button>
            <Button
              variant="quiet"
              size="sm"
              onClick={() => showUndo('Selection rejected', () => undefined)}
            >
              Reject
            </Button>
          </>
        }
        emptyState={
          activeFilterCount > 0 ? (
            <FilteredEmptyState
              isInset
              filterCount={activeFilterCount}
              onClearFilters={() => setSearch({ q: undefined, stage: undefined })}
            />
          ) : (
            <EmptyState
              isInset
              title="No candidates yet"
              body="Share the posting link and applications will appear here as they arrive."
              action={<Button variant="solid">Copy posting link</Button>}
            />
          )
        }
      />
    </>
  );
}

export interface DemoSearch {
  readonly q?: string;
  readonly stage?: DemoStage;
  /** `name:asc,score:desc` — short enough to read in the address bar. */
  readonly sort?: string;
}

function parseSort(raw: string | undefined): readonly SortSpec[] {
  if (raw === undefined || raw === '') return [];
  return raw
    .split(',')
    .map((part) => {
      const [columnId, direction] = part.split(':');
      if (!columnId) return null;
      return { columnId, direction: direction === 'desc' ? 'desc' : 'asc' } satisfies SortSpec;
    })
    .filter((spec): spec is SortSpec => spec !== null);
}

function serialiseSort(sort: readonly SortSpec[]): string | undefined {
  if (sort.length === 0) return undefined;
  return sort.map((spec) => `${spec.columnId}:${spec.direction}`).join(',');
}

function compareRows(a: DemoRow, b: DemoRow, columnId: string): number {
  switch (columnId) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'role':
      return a.role.localeCompare(b.role);
    case 'venture':
      return a.venture.localeCompare(b.venture);
    case 'stage':
      return a.stage.localeCompare(b.stage);
    case 'owner':
      return a.owner.localeCompare(b.owner);
    case 'appliedAt':
      return a.appliedAt.localeCompare(b.appliedAt);
    case 'score':
      // Nulls sort last in both directions — an absent score is not a low score.
      if (a.score === null && b.score === null) return 0;
      if (a.score === null) return 1;
      if (b.score === null) return -1;
      return a.score - b.score;
    case 'source':
      return (a.source ?? EMPTY).localeCompare(b.source ?? EMPTY);
    default:
      return 0;
  }
}
